import { useCallback, useEffect, useRef, useState } from 'react';

import * as botMeetingApi from '../services/botMeetingApi';
import * as meetingApi from '../services/meetingApi';
import { describeBotError } from '../utils/botMeetings';

// How long a stop the backend accepted keeps its button on "Ending...".
// The bot normally leaves, and the meeting finishes, within seconds; this
// only bounds the case where it does not, so the customer can press End
// Meeting again instead of staring at a disabled button.
export const LEAVING_GRACE_MS = 60000;

/**
 * Confirm-then-act for the two things an Auto Attend meeting offers on the
 * Meetings list and the meeting details page:
 *
 *   stop   - End Meeting while the bot is recording. Asks that one bot to
 *            leave (POST /api/bot-meetings/{id}/stop); the meeting is not
 *            ended by this request, the bot leaving ends it.
 *   cancel - Cancel Auto Attend before the bot has started recording.
 *
 * Both are separate from hooks/useEndMeeting.js on purpose: that PATCHes a
 * live meeting's status, which the backend refuses for a bot meeting.
 *
 * Usage: render <EndAutoAttendModal> while `stopTarget` is set and
 * <AutoAttendCancelModal> while `cancelTarget` is set.
 *
 * As in useEndMeeting, nothing is assumed: after the attempt the meeting is
 * re-read and handed to `onSettled({ kind, ok, meeting, target })` so the
 * caller shows what the backend now says. A failed attempt closes the
 * confirmation and leaves `actionError` for the page to show.
 */
export function useAutoAttendActions({ onSettled } = {}) {
  const [stopTarget, setStopTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState('');
  const [leavingIds, setLeavingIds] = useState([]);
  // A ref as well as state: two quick confirms must never send two requests.
  const inFlightRef = useRef(false);
  const timersRef = useRef(new Set());

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const markLeaving = useCallback((id) => {
    setLeavingIds((current) => (current.includes(id) ? current : [...current, id]));

    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      setLeavingIds((current) => current.filter((leavingId) => leavingId !== id));
    }, LEAVING_GRACE_MS);
    timersRef.current.add(timer);
  }, []);

  const run = useCallback(
    async (kind, target, perform, failureMessage) => {
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      setBusy({ id: target.id, kind });
      setActionError('');

      let ok = false;
      try {
        await perform(target.id);
        ok = true;
      } catch (err) {
        setActionError(describeBotError(err, failureMessage));
      }

      // The backend is the source of truth: whether the request worked,
      // was refused as stale (the bot had already moved on) or failed,
      // the row shows what it says now.
      let meeting = null;
      try {
        meeting = await meetingApi.getMeeting(target.id);
      } catch (_) {
        // The caller keeps what it had.
      }

      inFlightRef.current = false;
      setBusy(null);
      setStopTarget(null);
      setCancelTarget(null);
      if (ok && kind === 'stop') markLeaving(target.id);

      if (onSettled) onSettled({ kind, ok, meeting, target });
    },
    [markLeaving, onSettled]
  );

  const requestStop = useCallback((target) => {
    setActionError('');
    setStopTarget(target);
  }, []);

  const requestCancel = useCallback((target) => {
    setActionError('');
    setCancelTarget(target);
  }, []);

  const closeStop = useCallback(() => {
    if (inFlightRef.current) return;
    setStopTarget(null);
  }, []);

  const closeCancel = useCallback(() => {
    if (inFlightRef.current) return;
    setCancelTarget(null);
  }, []);

  const confirmStop = useCallback(() => {
    if (!stopTarget) return undefined;
    return run(
      'stop',
      stopTarget,
      botMeetingApi.stopBotMeeting,
      'Unable to end this meeting. Please try again.'
    );
  }, [run, stopTarget]);

  const confirmCancel = useCallback(() => {
    if (!cancelTarget) return undefined;
    return run(
      'cancel',
      cancelTarget,
      botMeetingApi.cancelBotMeeting,
      'Unable to cancel Auto Attend. Please try again.'
    );
  }, [run, cancelTarget]);

  return {
    stopTarget,
    cancelTarget,
    requestStop,
    requestCancel,
    closeStop,
    closeCancel,
    confirmStop,
    confirmCancel,
    // The id whose request is in flight right now, for disabling that
    // meeting's own button.
    busyId: busy ? busy.id : null,
    isStopping: busy?.kind === 'stop',
    isCancelling: busy?.kind === 'cancel',
    actionError,
    // Meetings whose bot has been asked to leave and has not yet.
    leavingIds,
  };
}
